import * as Sentry from "@sentry/browser";

/* Utility functions for making requests to hakai-api.
 * Appends appropriate user token data fetched from state.
 */

const isJSON = str => {
  try {
    return JSON.parse(str);
  } catch (e) {
    return false;
  }
};

function handleErrors(res) {
  if (!res.ok) {
    return (
      res
        // The API always returns JSON, but parsing the error as JSON caused
        // "Unexpected token in JSON" errors when Apache returned it's plain
        // text error instead.
        .text()
        .then(err => {
          // err is either HTML or JSON
          const errorMsg = isJSON(err)
            ? JSON.parse(err).message
            : res.statusText;

          // tell Sentry to not group errors that happen here
          // as they are just API errors caught here, not related to each other
          Sentry.configureScope(scope => {
            scope.setFingerprint(errorMsg);
            scope.setExtra("errMessageComplete", err);
          });
          throw new Error(errorMsg);
        })
    );
  }
  return res;
}

export const apiGet = (url, config) =>
  fetch(`${process.env.API_URL}${url}`, {
    credentials: "include",
    ...config
  }).then(handleErrors);
export const apiPost = (url, body, config) =>
  fetch(`${process.env.API_URL}${url}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
    ...config
  }).then(handleErrors);
export const apiPut = (url, body, config) =>
  fetch(`${process.env.API_URL}${url}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
    ...config
  }).then(handleErrors);
export const apiDelete = (url, config) =>
  fetch(`${process.env.API_URL}${url}`, {
    method: "DELETE",
    credentials: "include",
    ...config
  }).then(handleErrors);

export const apiPostFile = (url, body, config) =>
  fetch(`${process.env.API_URL}${url}`, {
    method: "POST",
    body,
    credentials: "include",
    ...config
  }).then(handleErrors);
export const apiPutFile = (url, body, config) =>
  fetch(`${process.env.API_URL}${url}`, {
    method: "PUT",
    body,
    credentials: "include",
    ...config
  }).then(handleErrors);

/* Accepts getURL, postURL, pkName, and postData. The function will first attempt to retrieve an
 * instances from the getURL. If successful, the object is updated and returned. If an object does
 * not exist at the getURL, a new object is posted to the postURL with the postData and returned.
 */
export const apiPutOrPost = (getURL, postURL, postData, pkName = "pk") => {
  let recordIsNew = false;

  // Return a promise that resolves to the instance object or throws an error
  const promise = new Promise((resolve, reject) => {
    // Try getting and returning the instance
    apiGet(getURL)
      .then(g => g.data[0])
      .then(gotRecord => {
        if (gotRecord) {
          const putURL = `${postURL}/${gotRecord[pkName]}`;
          // Instance already exists, update it
          return apiPut(putURL, postData);
        }
        // Instance doesn't exist, create and return it
        recordIsNew = true;
        return apiPost(postURL, postData);
      })
      .then(p => p.data)
      .then(updatedRecord => resolve({ new: recordIsNew, data: updatedRecord }))
      .catch(err => reject(err));
  });

  return promise;
};

/* Accepts getURL, postURL, and postData. The function will first attempt to retrieve an
 * instances from the getURL. If successful, this object is returned. If an object does not
 * exist at the getURL, a new object is posted to the postURL with the postData and returned.
 */
export const apiGetOrPost = (getURL, postURL, postData) => {
  let recordIsNew = false;

  // Return a promise that resolves to the instance object or throws an error
  const promise = new Promise((resolve, reject) => {
    // Try getting and returning the instance
    apiGet(getURL)
      .then(g => {
        // Instance already exists, return it
        if (g.data[0]) return g;

        // Instance doesn't exist, create and return it
        recordIsNew = true;
        return apiPost(postURL, postData);
      })
      .then(p => p.data)
      .then(record => resolve({ new: recordIsNew, data: record }))
      .catch(err => reject(err));
  });

  return promise;
};
